import { useRef } from 'react';
import { useSlideDeckPdf } from './useSlideDeckPdf';
import { SchoolDeckMetrics } from './schoolReportDeck';
import styles from './SchoolReportPDFSlides.module.scss';
import iconStaff from '../../images/schoolReportDeck/icon-staff.png';
import iconStudents from '../../images/schoolReportDeck/icon-students.png';
import iconLessons from '../../images/schoolReportDeck/icon-lessons.png';
import iconStories from '../../images/schoolReportDeck/icon-stories.png';
import iconActivities from '../../images/schoolReportDeck/icon-activities.png';
import iconCheckins from '../../images/schoolReportDeck/icon-checkins.png';
import iconAssignments from '../../images/schoolReportDeck/icon-assignments.png';
import iconDay from '../../images/schoolReportDeck/icon-day.png';

export const ELEMENTARY_STACKED_ORDER = ['Excited', 'Happy', 'Calm', 'Nervous', 'Sad', 'Angry'];
export const ELEMENTARY_STACKED_COLORS: Record<string, string> = {
  Angry: '#03afd8',
  Sad: '#47c6ea',
  Nervous: '#c4e9f3',
  Calm: '#fee0d2',
  Happy: '#ff7731',
  Excited: '#ff5300',
};

export const SECONDARY_STACKED_ORDER = ['Surprised', 'Confident', 'Happy', 'Calm', 'Sad', 'Scared', 'Angry'];
export const SECONDARY_STACKED_COLORS: Record<string, string> = {
  Angry: '#171497',
  Scared: '#5881bc',
  Sad: '#6eb8e3',
  Calm: '#bfe4e3',
  Happy: '#fdf0aa',
  Confident: '#f58855',
  Surprised: '#ff5300',
};

export const ELEMENTARY_MOOD_COLORS: Record<string, string> = {
  Excited: '#ff5100',
  Happy: '#ff7730',
  Calm: '#ffe1d2',
  Nervous: '#c5e9f3',
  Sad: '#45c6eb',
  Angry: '#00afd8',
};

export const ELEMENTARY_MOOD_TEXT_COLORS: Record<string, string> = {
  Excited: '#ffffff',
  Happy: '#ffffff',
  Calm: '#000000',
  Nervous: '#000000',
  Sad: '#ffffff',
  Angry: '#ffffff',
};

export const SECONDARY_MOOD_COLORS: Record<string, string> = {
  Surprised: '#ff5100',
  Confident: '#f58955',
  Happy: '#fdf0aa',
  Calm: '#bfe5e4',
  Sad: '#6db8e3',
  Scared: '#5982bc',
  Angry: '#181597',
};

export const SECONDARY_MOOD_TEXT_COLORS: Record<string, string> = {
  Surprised: '#ffffff',
  Confident: '#ffffff',
  Happy: '#000000',
  Calm: '#000000',
  Sad: '#e7f4f7',
  Scared: '#e7f4f7',
  Angry: '#e7f4f7',
};

const DAY_BAR_COLORS = ['#ff5100'];
export const GAME_BAR_COLORS = ['#4285f4'];
export const STORY_BAR_COLORS = ['#d32f2f'];

interface DeckProps {
  metrics: SchoolDeckMetrics;
  fileName: string;
  onComplete: () => void;
}

export interface BarItem {
  label: string;
  value: number;
}

// Month labels come in as "Aug-2025". Past ~10 months they're wider than their bar column, so put
// the year on its own line instead of letting neighbouring labels run into each other - and past
// ~2 years even "2025" doesn't fit a column, so shorten it to "'25".
const STACK_MONTH_LABELS_OVER = 10;
const SHORT_YEAR_LABELS_OVER = 24;
const MonthLabel = ({ label, count }: { label: string; count: number }) => {
  const [month, year] = label.split('-');
  if (count <= STACK_MONTH_LABELS_OVER || !year) return <>{label}</>;
  return (
    <>
      {month}
      <br />
      {count > SHORT_YEAR_LABELS_OVER ? `'${year.slice(-2)}` : year}
    </>
  );
};

const monthLabelSize = (count: number) => (count > 18 ? 10 : count > 10 ? 11 : count > 6 ? 13 : 15);

export const roundToNice = (value: number) => {
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(1, value))));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
};

export const VerticalBarChart = ({
  items,
  color,
  colors,
  width = 840,
  showGrid = false,
}: {
  items: BarItem[];
  color?: string;
  colors?: Record<string, string>;
  width?: number;
  showGrid?: boolean;
}) => {
  const max = Math.max(1, ...items.map((item) => item.value));
  // Snug to the actual max rather than rounding up to the next "nice" step (roundToNice) - for a
  // small max (e.g. a handful of educators logging in per month), that rounding pads the axis by a
  // large relative amount, leaving every bar looking short even though it's already the tallest.
  const tickMax = max;
  const rawTicks =
    tickMax <= 4
      ? Array.from({ length: tickMax + 1 }, (_, i) => tickMax - i)
      : [tickMax, (tickMax * 2) / 3, tickMax / 3, 0];
  const ticks = Array.from(new Set(rawTicks.map((t) => Math.round(t))));
  const count = items.length;
  const gap = count > 10 ? 4 : count > 6 ? 8 : 14;
  const plotWidth = width * 0.91;
  const barWidth = Math.max(6, Math.min(38, Math.floor((plotWidth - gap * (count - 1)) / count)));
  const labelSize = monthLabelSize(count);
  const valueSize = count > 8 ? 12 : 16;
  return (
    <div className={styles.verticalChart}>
      <div className={styles.chartYAxis}>
        {ticks.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </div>
      <div className={styles.chartYTitle}>Count</div>
      {showGrid && (
        <div className={styles.chartGridLines}>
          {ticks.map((tick) => (
            <div key={tick} className={styles.chartGridLine} />
          ))}
        </div>
      )}
      <div className={styles.verticalChartRow} style={{ gap }}>
        {items.map((item) => (
          <div className={styles.verticalBarCol} key={item.label}>
            <div className={styles.verticalBarTrack} style={{ width: barWidth }}>
              <div
                className={styles.verticalBar}
                style={{
                  height: item.value > 0 ? `${Math.max(2, (item.value / tickMax) * 100)}%` : '0%',
                  backgroundColor: colors?.[item.label] || color,
                }}
              >
                <div className={styles.verticalBarValue} style={{ fontSize: valueSize }}>
                  {item.value}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* A separate row below the plot, not another child sharing the bar column's own height - the
          month label used to live inside verticalBarCol, forcing the bar track to be a fixed 23px
          shorter than the row so the label wouldn't get squeezed out. That shrank and shifted the
          bars' own 0-100% scale relative to the gridlines/axis (which still span the full row), so a
          bar could end up taller, and higher, than a gridline for an even bigger value. */}
      <div className={styles.verticalChartLabels} style={{ gap }}>
        {items.map((item) => (
          <div className={styles.verticalBarLabelCol} key={item.label}>
            <div className={styles.verticalBarLabel} style={{ fontSize: labelSize }}>
              <MonthLabel label={item.label} count={count} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const HorizontalBarChart = ({ items, colors }: { items: BarItem[]; colors: string[] }) => {
  const max = Math.max(1, ...items.map((item) => item.value));
  const tickMax = roundToNice(max);
  const ticks = [0, tickMax / 2, tickMax];
  return (
    <div className={styles.horizontalChart}>
      <div className={styles.horizontalYLabels}>
        {items.map((item) => (
          <span key={item.label}>{item.label}</span>
        ))}
      </div>
      <div className={styles.horizontalChartInner}>
        {items.map((item, index) => (
          <div className={styles.horizontalRow} key={item.label}>
            <div className={styles.horizontalTrack}>
              <div
                className={styles.horizontalBar}
                style={{
                  width: item.value > 0 ? `${Math.max(1, (item.value / tickMax) * 100)}%` : '0%',
                  backgroundColor: colors[index % colors.length],
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className={styles.horizontalXLabels}>
        {ticks.map((tick) => (
          <span key={tick}>{Math.round(tick)}</span>
        ))}
      </div>
    </div>
  );
};

export interface MoodSeries {
  month: string;
  counts: Record<string, number>;
}

export const MoodStackedBarChart = ({
  series,
  order,
  colors,
  width = 773,
}: {
  series: MoodSeries[];
  order: string[];
  colors: Record<string, string>;
  width?: number;
}) => {
  const maxTotal = Math.max(
    1,
    ...series.map((month) => order.reduce((sum, mood) => sum + (month.counts[mood] || 0), 0)),
  );
  const tickMax = roundToNice(maxTotal);
  const ticks = [tickMax, tickMax / 2, 0];
  const count = series.length;
  const gap = count > 10 ? 4 : count > 6 ? 8 : 14;
  const plotWidth = width * 0.88;
  const barWidth = Math.max(6, Math.min(40, Math.floor((plotWidth - gap * (count - 1)) / count)));
  const labelSize = monthLabelSize(count);
  const stackLabels = count > STACK_MONTH_LABELS_OVER;
  return (
    <div className={styles.stackedChart}>
      <div className={styles.chartYTitle}>Count</div>
      <div className={styles.stackedYAxis}>
        {ticks.map((t) => (
          <span key={t}>{Math.round(t)}</span>
        ))}
      </div>
      <div className={styles.stackedInner} style={{ gap }}>
        {series.map((month) => {
          const monthTotal = order.reduce((sum, mood) => sum + (month.counts[mood] || 0), 0);
          return (
            <div className={styles.stackedCol} key={month.month}>
              <div className={styles.stackedTrack} style={{ width: barWidth }}>
                <div
                  className={styles.stackedSegments}
                  style={{ height: `${monthTotal ? Math.max(2, (monthTotal / tickMax) * 100) : 0}%` }}
                >
                  {order.map((mood) => {
                    const val = month.counts[mood] || 0;
                    if (!val) return null;
                    return (
                      <div
                        key={mood}
                        className={styles.stackedSegment}
                        style={{
                          flex: `${val} 0 0`,
                          backgroundColor: colors[mood],
                        }}
                      />
                    );
                  })}
                </div>
              </div>
              <div
                className={styles.stackedLabel}
                style={{ fontSize: labelSize, marginTop: stackLabels ? 3 : undefined }}
              >
                <MonthLabel label={month.month} count={count} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const MoodDirection = ({ percent }: { percent: number }) => (
  <strong className={styles.upMood}>{percent}% up mood</strong>
);

export const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const OVERVIEW_METRIC_LAYOUT: {
  key: keyof SchoolDeckMetrics['overview'];
  label: string;
  icon: string;
  x: number;
  y: number;
  width: number;
  iconX: number;
  iconY: number;
  iconSize: number;
  size: number;
}[] = [
  {
    label: 'Staff',
    key: 'staff',
    icon: iconStaff,
    x: 180,
    y: 238,
    width: 126,
    iconX: 60,
    iconY: 215,
    iconSize: 111,
    size: 27,
  },
  {
    label: 'Students',
    key: 'students',
    icon: iconStudents,
    x: 175,
    y: 383,
    width: 179,
    iconX: 58,
    iconY: 371,
    iconSize: 111,
    size: 28,
  },
  {
    label: 'Assignments',
    key: 'assignments',
    icon: iconAssignments,
    x: 171,
    y: 545,
    width: 201,
    iconX: 60,
    iconY: 545,
    iconSize: 100,
    size: 28,
  },
  {
    label: 'Lessons',
    key: 'lessons',
    icon: iconLessons,
    x: 528,
    y: 223,
    width: 201,
    iconX: 394,
    iconY: 215,
    iconSize: 111,
    size: 28,
  },
  {
    label: 'Stories',
    key: 'stories',
    icon: iconStories,
    x: 528,
    y: 376,
    width: 179,
    iconX: 394,
    iconY: 371,
    iconSize: 111,
    size: 28,
  },
  {
    label: 'Activities',
    key: 'activities',
    icon: iconActivities,
    x: 528,
    y: 545,
    width: 164,
    iconX: 391,
    iconY: 529,
    iconSize: 111,
    size: 28,
  },
  {
    label: 'Check-ins',
    key: 'checkins',
    icon: iconCheckins,
    x: 869,
    y: 223,
    width: 187,
    iconX: 740,
    iconY: 215,
    iconSize: 111,
    size: 28,
  },
];

// The seven icon + label + value blocks on the overview slide, positioned to match the deck template.
export const OverviewMetrics = ({ overview }: { overview: SchoolDeckMetrics['overview'] }) => (
  <>
    {OVERVIEW_METRIC_LAYOUT.map((metric) => (
      <div key={metric.label}>
        <img
          src={metric.icon}
          alt=''
          className={styles.overviewMetricImg}
          style={{
            position: 'absolute',
            left: metric.iconX,
            top: metric.iconY,
            width: metric.iconSize,
            height: metric.iconSize,
          }}
        />
        <div
          className={styles.overviewMetric}
          style={{ left: metric.x, top: metric.y, width: metric.width, fontSize: metric.size }}
        >
          <span className={styles.overviewMetricLabel}>{metric.label}</span>
          <span className={styles.overviewMetricValue}>{overview[metric.key]}</span>
        </div>
      </div>
    ))}
  </>
);

// A single-line title has to fit a fixed-width slide regardless of how long the name is, so scale
// the font down for longer names instead of letting it overflow or wrap unpredictably.
export const fitTitleFontSize = (text: string, base: number) => {
  const ratio = text.length > 28 ? 0.485 : text.length > 20 ? 0.6 : text.length > 13 ? 0.75 : 1;
  return Math.round(base * ratio);
};

// ---------- Slides shared with the district deck ----------

const ELEMENTARY_CHIP_STOPS = [115, 212, 309, 406, 503, 600];
const SECONDARY_CHIP_STOPS = [91, 179, 266, 354, 441, 529, 616];

interface MoodSlideProps {
  dateRangeLabel: string;
  moodsByMonth: MoodSeries[];
  moods: { mood: string; percent: number }[];
  upMoodPercent: number;
  students: number;
}

export const ElementaryMoodSlide = ({
  dateRangeLabel,
  moodsByMonth: elementaryMoodsByMonth,
  moods: elementaryMoods,
  upMoodPercent: elementaryUpMoodPercent,
  students: elementaryStudents,
}: MoodSlideProps) => (
  <div className={styles.slide} data-deck-slide>
    <div className={styles.blueSlideTitle} style={{ top: 69 }}>
      Feelings Check-in - Elementary
    </div>
    <div className={styles.moodPeriod}>{dateRangeLabel}</div>
    <div className={styles.moodChartArea}>
      <MoodStackedBarChart
        series={elementaryMoodsByMonth}
        order={ELEMENTARY_STACKED_ORDER}
        colors={ELEMENTARY_STACKED_COLORS}
      />
    </div>
    <p className={styles.moodIntro} style={{ left: 1030, top: 302 }}>
      The <MoodDirection percent={elementaryUpMoodPercent} /> for K-5 is representative of{' '}
      {pluralize(elementaryStudents, 'student')}
    </p>
    {elementaryMoods.map((item, index) => (
      <div
        key={item.mood}
        className={styles.moodChip}
        style={{
          left: 869,
          top: ELEMENTARY_CHIP_STOPS[index],
          width: 155,
          height: 90,
          backgroundColor: ELEMENTARY_MOOD_COLORS[item.mood],
          color: ELEMENTARY_MOOD_TEXT_COLORS[item.mood],
        }}
      >
        <span className={styles.moodChipPercent} style={{ fontSize: 28 }}>
          {item.percent}%
        </span>
        <span className={styles.moodChipName} style={{ fontSize: 21 }}>
          {item.mood}
        </span>
      </div>
    ))}
  </div>
);

export const SecondaryMoodSlide = ({
  dateRangeLabel,
  moodsByMonth: secondaryMoodsByMonth,
  moods: secondaryMoods,
  upMoodPercent: secondaryUpMoodPercent,
  students: secondaryStudents,
}: MoodSlideProps) => (
  <div className={styles.slide} data-deck-slide>
    <div className={styles.blueSlideTitle} style={{ top: 69 }}>
      Feelings Check-in - Secondary
    </div>
    <div className={styles.moodPeriod}>{dateRangeLabel}</div>
    <div className={styles.moodChartArea}>
      <MoodStackedBarChart
        series={secondaryMoodsByMonth}
        order={SECONDARY_STACKED_ORDER}
        colors={SECONDARY_STACKED_COLORS}
      />
    </div>
    <p className={styles.moodIntro} style={{ left: 1015, top: 301 }}>
      The <MoodDirection percent={secondaryUpMoodPercent} /> for 6-12 is representative of{' '}
      {pluralize(secondaryStudents, 'student')}
    </p>
    {secondaryMoods.map((item, index) => (
      <div
        key={item.mood}
        className={styles.moodChip}
        style={{
          left: 883,
          top: SECONDARY_CHIP_STOPS[index],
          width: 120,
          height: 79,
          backgroundColor: SECONDARY_MOOD_COLORS[item.mood],
          color: SECONDARY_MOOD_TEXT_COLORS[item.mood],
        }}
      >
        <span className={styles.moodChipPercent} style={{ fontSize: 27 }}>
          {item.percent}%
        </span>
        <span className={styles.moodChipName} style={{ fontSize: 20 }}>
          {item.mood}
        </span>
      </div>
    ))}
  </div>
);

export const DayOfWeekSlide = ({
  dateRangeLabel,
  educatorDayOfWeek,
  mostUsedDay,
}: {
  dateRangeLabel: string;
  educatorDayOfWeek: { day: string; count: number }[];
  mostUsedDay: string;
}) => (
  <div className={styles.slide} data-deck-slide>
    <div className={styles.slideTitle}>When are educators using Peekapak?</div>
    <div className={styles.slideSubtitle}>{dateRangeLabel}</div>
    <div className={styles.chartImageArea}>
      <HorizontalBarChart
        items={educatorDayOfWeek.map((item) => ({ label: item.day, value: item.count }))}
        colors={DAY_BAR_COLORS}
      />
    </div>
    <div className={styles.dayText}>
      <strong>{mostUsedDay}</strong> is the most popular for Peekapak time!
    </div>
    <img className={styles.dayIcon} src={iconDay} alt='' />
  </div>
);

export const GamesSlide = ({
  dateRangeLabel,
  topGames,
  activities,
}: {
  dateRangeLabel: string;
  topGames: { name: string; plays: number }[];
  activities: number;
}) => (
  <div className={styles.slide} data-deck-slide>
    <div className={styles.slideTitle}>Most popular games</div>
    <div className={styles.slideSubtitle}>{dateRangeLabel}</div>
    <div className={styles.chartImageArea}>
      <HorizontalBarChart
        items={topGames.map((item) => ({ label: item.name, value: item.plays }))}
        colors={GAME_BAR_COLORS}
      />
    </div>
    <img className={styles.statIcon} src={iconActivities} alt='' style={{ left: 1004, top: 520 }} />
    <div className={styles.gamesText}>
      <strong className={styles.statStrong}>{activities}</strong> games played
    </div>
  </div>
);

export const StoriesSlide = ({
  dateRangeLabel,
  topStories,
  stories,
}: {
  dateRangeLabel: string;
  topStories: { name: string; reads: number }[];
  stories: number;
}) => (
  <div className={styles.slide} data-deck-slide>
    <div className={styles.slideTitle}>Most popular stories</div>
    <div className={styles.slideSubtitle}>{dateRangeLabel}</div>
    <div className={styles.chartImageArea}>
      <HorizontalBarChart
        items={topStories.map((item) => ({ label: item.name, value: item.reads }))}
        colors={STORY_BAR_COLORS}
      />
    </div>
    <img className={styles.statIcon} src={iconStories} alt='' style={{ left: 1004, top: 520 }} />
    <div className={styles.statText}>
      <strong className={styles.statStrong}>{stories}</strong> stories read
    </div>
  </div>
);

const SchoolReportPDFSlides = ({ metrics, fileName, onComplete }: DeckProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useSlideDeckPdf(containerRef, fileName, onComplete, 'school report');

  const {
    schoolName,
    dateRangeLabel,
    overview,
    educatorLoginsTotal,
    studentLoginsTotal,
    educatorLoginsByMonth,
    studentLoginsByMonth,
    educatorDayOfWeek,
    mostUsedDay,
    topEducators,
    elementaryMoods,
    elementaryUpMoodPercent,
    elementaryStudents,
    elementaryMoodsByMonth,
    secondaryMoods,
    secondaryUpMoodPercent,
    secondaryStudents,
    secondaryMoodsByMonth,
    topGames,
    topStories,
  } = metrics;

  const coverTitleFontSize = fitTitleFontSize(schoolName, 107);
  const overviewTitleText = `${schoolName} - Overview`;
  const overviewTitleFontSize = 52;

  return (
    <div ref={containerRef} className={styles.pdfContainer}>
      <div className={styles.slide} data-deck-slide>
        <div className={styles.coverBand} />
        <div className={styles.coverContent}>
          <h1 className={styles.coverTitle} style={{ fontSize: coverTitleFontSize }}>
            {schoolName}
          </h1>
        </div>
        <div className={styles.coverDate}>{dateRangeLabel}</div>
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.largeSlideTitle} style={{ fontSize: overviewTitleFontSize }}>
          {overviewTitleText}
        </div>
        <div className={styles.overviewPeriod}>{dateRangeLabel}</div>
        <OverviewMetrics overview={overview} />
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.blueSlideTitle}>Educator Engagement - Whole School</div>
        <div className={styles.overviewPeriod}>{dateRangeLabel}</div>
        <div className={styles.chartImageArea}>
          <VerticalBarChart
            items={educatorLoginsByMonth.map((item) => ({ label: item.month, value: item.logins }))}
            color='#2b6cb0'
            showGrid
          />
        </div>
        <div className={styles.totalBox}>
          <div className={styles.totalBoxLabel}>Total:</div>
          <div className={styles.totalBoxValue}>{educatorLoginsTotal}</div>
        </div>
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.blueSlideTitle}>Student Engagement - Whole School</div>
        <div className={styles.overviewPeriod}>{dateRangeLabel}</div>
        <div className={styles.chartImageArea}>
          <VerticalBarChart
            items={studentLoginsByMonth.map((item) => ({ label: item.month, value: item.logins }))}
            color='#fb542b'
            showGrid
          />
        </div>
        <div className={styles.totalBox}>
          <div className={styles.totalBoxLabel}>Total:</div>
          <div className={styles.totalBoxValue}>{studentLoginsTotal}</div>
        </div>
      </div>

      <div className={styles.slide} data-deck-slide>
        <div className={styles.powerUsersTitle}>
          Peekapak Power Users:
          <br />
          Our Most Engaged Educators!
        </div>
        <div className={styles.slideSubtitle} style={{ top: 146 }}>
          Below are educators with the most logins at {schoolName}.
        </div>
        <table className={styles.powerUsersTable}>
          <thead>
            <tr>
              <th colSpan={2}>{schoolName}</th>
            </tr>
          </thead>
          <tbody>
            {topEducators.map((educator) => (
              <tr key={educator.name}>
                <td>{educator.name}</td>
                <td className={styles.powerUsersCount}>{educator.logins}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ElementaryMoodSlide
        dateRangeLabel={dateRangeLabel}
        moodsByMonth={elementaryMoodsByMonth}
        moods={elementaryMoods}
        upMoodPercent={elementaryUpMoodPercent}
        students={elementaryStudents}
      />
      <SecondaryMoodSlide
        dateRangeLabel={dateRangeLabel}
        moodsByMonth={secondaryMoodsByMonth}
        moods={secondaryMoods}
        upMoodPercent={secondaryUpMoodPercent}
        students={secondaryStudents}
      />
      <DayOfWeekSlide dateRangeLabel={dateRangeLabel} educatorDayOfWeek={educatorDayOfWeek} mostUsedDay={mostUsedDay} />
      <GamesSlide dateRangeLabel={dateRangeLabel} topGames={topGames} activities={overview.activities} />
      <StoriesSlide dateRangeLabel={dateRangeLabel} topStories={topStories} stories={overview.stories} />
    </div>
  );
};

export default SchoolReportPDFSlides;
